<script setup lang="ts">
/**
 * Onboarding tour — step 3 body (M4C-16).
 *
 * "Fragments are the small wins": a scattered row of cyan fragment sparkles
 * (the reusable `GamiFragment` atom) above the heading, a Body-Strong lead and a
 * list of fragment-earning actions (one column on mobile, two on desktop). The
 * chrome (progress header, footer nav, border, backdrop) is owned by the tour
 * shell (M4C-13); this component renders body content only.
 */
const { t, tm, rt } = useI18n();

/**
 * Fragment-earning actions. The chip list that renders these is hidden for now
 * (M4C-50) but kept wired here so it's a one-line restore in the template.
 */
const actions = computed(() => tm('onboarding.tour.step3.actions') as string[]);

/**
 * Decorative sparkle field. Percentage `left` scales the row with the card
 * width; `top` (px, within the 176px block) gives the zigzag. Sparkles without
 * `mobile` are hidden on the narrow card so the row stays sparse there.
 */
const sparkles: { left: string; top: number; mobile?: boolean }[] = [
  { left: '2%', top: 44, mobile: true },
  { left: '11%', top: 86 },
  { left: '20%', top: 52 },
  { left: '28%', top: 96, mobile: true },
  { left: '37%', top: 48 },
  { left: '46%', top: 90, mobile: true },
  { left: '54%', top: 56 },
  { left: '62%', top: 98, mobile: true },
  { left: '71%', top: 50 },
  { left: '79%', top: 92 },
  { left: '87%', top: 54, mobile: true },
  { left: '95%', top: 88 },
];
</script>

<template>
  <div class="flex flex-col gap-16">
    <!-- Scattered fragment sparkles -->
    <div class="relative h-[176px] w-full" aria-hidden="true">
      <GamiFragment
        v-for="(sparkle, index) in sparkles"
        :key="index"
        :size="16"
        class="absolute"
        :class="{ 'hidden md:block': !sparkle.mobile }"
        :style="{ left: sparkle.left, top: `${sparkle.top}px` }"
      />
    </div>

    <!-- Heading + body copy (40px gap) -->
    <div class="flex flex-col gap-10">
      <p class="text-h1">{{ t('onboarding.tour.step3.heading') }}</p>

      <div class="flex flex-col items-start gap-6 md:flex-row md:gap-4">
        <p class="text-body-strong md:w-[449px] md:shrink-0">
          {{ t('onboarding.tour.step3.body') }}
        </p>
        <!-- [M4C-50] Action chip list hidden for now; data kept in i18n for easy restore. -->
        <!-- <ul class="text-microcopy md:flex-1 md:columns-2">
          <li
            v-for="action in actions"
            :key="String(action)"
            class="flex break-inside-avoid items-center gap-2"
          >
            <span class="h-1 w-1 shrink-0 rounded-full bg-current" aria-hidden="true" />
            <span>{{ rt(action) }}</span>
          </li>
        </ul> -->
      </div>
    </div>
  </div>
</template>
